import {
  Badge,
  Card,
  CardContent,
  cn,
  KpiCard,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { useReducedMotion } from 'motion/react';

import {
  statusBoardCardClass,
  statusBoardCardPadClass,
} from './dashboard-board-surfaces';
import type { DashboardPeriodChange } from './dashboard-status-types';

export type DashboardKpiTone = 'brand' | 'ok' | 'warning' | 'rating';

export type DashboardKpiItem = {
  id: string;
  label: string;
  value: string;
  sub: string;
  badge?: string;
  badgeTone?: 'ok' | 'warning' | 'neutral';
  /**
   * Real period series (0–1). Omit when there is no historical event series —
   * never pass decorative flat zeros.
   */
  spark?: number[];
  periodChange?: DashboardPeriodChange;
  periodChangeLabel?: string;
  tone: DashboardKpiTone;
  href?: string;
};

const BADGE_VARIANT = {
  ok: 'success',
  warning: 'warning',
  neutral: 'secondary',
} as const;

/**
 * KPI strip columns track visible count so omitted modules (permission /
 * entitlement nulls) do not leave empty 4-up slots.
 * - Mobile: 2-up (1 → single full-width).
 * - Desktop: 1→1, 2→2, 3→2 then xl:3, 4→2 then xl:4.
 * Mid widths stay ≤2-col so values never character-stack in narrow tracks.
 */
export function kpiStripGridColsClass(
  count: number,
  isMobile: boolean,
): string {
  if (count <= 1) return 'grid-cols-1';
  if (isMobile) return 'grid-cols-2';
  if (count === 2) return 'grid-cols-2';
  if (count === 3) return 'grid-cols-2 xl:grid-cols-3';
  return 'grid-cols-2 xl:grid-cols-4';
}

export function DashboardKpiStrip({
  items,
  isLoading,
  isMobile,
  rangeDays,
  onOpen,
  loadingLabel,
}: {
  items: DashboardKpiItem[];
  isLoading: boolean;
  isMobile: boolean;
  /** Drives sparkline remount when 7/30/90 changes. */
  rangeDays: number;
  onOpen: (href: string) => void;
  loadingLabel: string;
}) {
  const reduceMotion = useReducedMotion() ?? false;
  // Loading: assume full 4-up until capabilities resolve.
  const columnCount = isLoading ? 4 : Math.max(items.length, 1);
  const gridClass = cn(
    'grid min-w-0 gap-2.5 md:gap-3.5',
    kpiStripGridColsClass(columnCount, isMobile),
  );

  if (isLoading) {
    return (
      <section aria-busy="true" aria-label={loadingLabel} className={gridClass}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i} className={statusBoardCardClass}>
            <CardContent className={cn('space-y-3', statusBoardCardPadClass)}>
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </section>
    );
  }

  return (
    <section aria-label={loadingLabel} className={gridClass}>
      {items.map((item) => (
        <KpiCard
          key={item.id}
          label={item.label}
          badge={
            item.badge ? (
              <Badge variant={BADGE_VARIANT[item.badgeTone ?? 'neutral']}>
                {item.badge}
              </Badge>
            ) : undefined
          }
          value={item.value}
          sub={item.sub}
          change={
            item.periodChangeLabel
              ? {
                  label: item.periodChangeLabel,
                  direction:
                    item.periodChange?.direction === 'up' ||
                    item.periodChange?.direction === 'down'
                      ? item.periodChange.direction
                      : 'flat',
                }
              : undefined
          }
          spark={item.spark && item.spark.length > 0 ? item.spark : undefined}
          sparkTone={item.tone}
          sparkAnimationKey={
            reduceMotion
              ? undefined
              : `${item.id}-${rangeDays}-${(item.spark ?? []).join(',')}`
          }
          onClick={item.href ? () => onOpen(item.href!) : undefined}
        />
      ))}
    </section>
  );
}
